import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BookingModePicker } from "@/components/site/BookingModePicker";
import {
  getAvailableBookingModes,
  getBookingUrl,
  type BookingAction,
  type BookingMode,
} from "@/data/certificateBookingActions";
import { useBookingConfirmationRedirect } from "@/hooks/useBookingConfirmationRedirect";
import { useLang } from "@/i18n/LangContext";

type BookingEmbedProps = {
  action: BookingAction;
  title: string;
  /** Internal certificate slug — used to show the post-booking form on /bekraftelse */
  slug?: string | null;
  iframeClassName?: string;
  initialMode?: BookingMode | null;
};

const resolveInitialMode = (action: BookingAction, initialMode: BookingMode | null) => {
  const modes = getAvailableBookingModes(action);
  if (initialMode && modes.includes(initialMode)) return initialMode;
  return modes.length === 1 ? modes[0]! : null;
};

export const BookingEmbed = ({
  action,
  title,
  slug = null,
  iframeClassName = "h-[70vh] min-h-[520px] w-full border-0",
  initialMode = null,
}: BookingEmbedProps) => {
  const { lang } = useLang();
  const modes = getAvailableBookingModes(action);
  const [mode, setMode] = useState<BookingMode | null>(() => resolveInitialMode(action, initialMode));

  useEffect(() => {
    setMode(resolveInitialMode(action, initialMode));
  }, [action, initialMode]);

  const url = mode ? getBookingUrl(action, mode) : null;
  useBookingConfirmationRedirect({ active: Boolean(url), slug, mode });

  if (!url) {
    return <BookingModePicker modes={modes} onSelect={setMode} />;
  }

  return (
    <div className="space-y-3">
      {modes.length > 1 ? (
        <Button type="button" variant="ghost" size="sm" className="-ml-2 text-muted-foreground" onClick={() => setMode(null)}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          {lang === "sv" ? "Byt besökstyp" : "Change visit type"}
        </Button>
      ) : null}
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <iframe
          src={url}
          title={title}
          className={iframeClassName}
          loading="lazy"
          allow="payment; clipboard-write"
        />
      </div>
    </div>
  );
};
